
































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import ArrowIcon from '../../../../assets/svg/web-conferences/arrow-up.svg';
import AgoraRTC from "agora-rtc-sdk-ng";
import {AgoraVideoEncoding} from "@share/web-conferences/enums/AgoraVideoEncoding";
import {JoinHandlerInterface} from "@share/web-conferences/interfaces/JoinHandlerInterface";
import ButtonWithContext from "@share/web-conferences/components/common/ButtonWithContext.vue";
import ItemsList from "@share/web-conferences/components/common/ItemsList.vue";
import {SharingTypes} from "@api/enums/WebConferenceSlides/SharingTypes";
import PdfIcon from "@share/web-conferences/components/control-panel/share-icons/PdfIcon.vue";
import ScreenSharingIcon from "@share/web-conferences/components/control-panel/share-icons/ScreenSharingIcon.vue";
import {WebConferenceService} from "@share/web-conferences/data/WebConferenceService";
import {UserProfile} from "@api/models/UserProfile";
import {showCantJoinShareMessage, showCantShareMessage} from "@share/web-conferences/helpers/ShareModals";
import {findSharingTrackIndex} from "@share/web-conferences/helpers/IsShareScreen";
import RingLoader from "@share/common/RingLoader.vue";

@Component({
  name: "ControlPanelShare",
  components: {RingLoader, ItemsList, ButtonWithContext, ArrowIcon, PdfIcon, ScreenSharingIcon }
})
export default class ControlPanelShare extends Vue {
    @Prop({default: false}) isActive: boolean;
    @Prop({default: false}) disabled: boolean;
    @Prop({required: true}) joinHandler: JoinHandlerInterface;
    @Prop({required: true}) webConferenceService: WebConferenceService;
    @Prop({default: true}) canSharePdf: boolean;
    @Prop({default: false}) buttonMode: boolean;

    isSharingSwitching = false;
    isSharingBeginStopped = false;
    options = [
        {
            key: SharingTypes.Slides,
            iconComponent: 'PdfIcon'
        },
        {
            key: SharingTypes.Screen,
            iconComponent: 'ScreenSharingIcon'
        }
    ];

    get label() {
        return this.$t('web-conferences.share');
    }

    get filteredOptions() {
        return this.options.filter(item => {
            return this.canSharePdf ? true : item.key != SharingTypes.Slides;
        });
    }

    get currentUserProfile(): UserProfile {
        return this.$store.getters.profile;
    }

    get isCurrentPresentationActive() {
        return this.webConferenceService.webConferenceData?.currentPresentation != null;
    }

    get isSharing() {
        return this.webConferenceService.webConferenceData?.currentPresentation?.userId === this.currentUserProfile.userId;
    }

    get isThereSharingUser() {
        return !this.isSharing && this.webConferenceService.webConferenceData?.isSharing();
    }

    get isThereSharingTracks() {
        return findSharingTrackIndex(this.joinHandler.agora.tracksManager.tracks) >= 0;
    }

    isSharingScreen() {
        return (this.isSharing && this.webConferenceService.webConferenceData?.hasSharingType(SharingTypes.Screen)) ||
               this.joinHandler.shareClient.isSharing;
    }

    async onShare(type: SharingTypes) {
        if (type == SharingTypes.Screen) {
            await this.onShareScreen();
        }
        else {
            this.$emit('sharePdf');
        }
    }

    mounted() {
        if (
            this.webConferenceService.webConferenceData?.hasSharingType(SharingTypes.Screen) &&
            this.webConferenceService.webConferenceData?.isSharingUser(this.currentUserProfile.userId as number)
        ) {
            this.runScreenSharing();
        }
    }

    async onLabelClick() {
        if (this.isSharing) {
          await this.stopSharing();
        }
    }

    async onShareScreen() {
        if (this.isSharingSwitching) {
            return;
        }

        this.isSharingSwitching = true;

        if (this.isSharing) {
            await this.joinHandler.shareClient.leave();
        }
        else {
            await this.runScreenSharing();
        }

        this.isSharingSwitching = false;
    }

    async runScreenSharing() {
        try {
            const tracks = await AgoraRTC.createScreenVideoTrack({
                encoderConfig: AgoraVideoEncoding.Ve1080p1,
                screenSourceType: 'screen'
            }, "auto");
            const joinResult = await this.joinHandler.joinScreenShareClient();

            if (joinResult) {
                await this.joinHandler.shareClient.createStream(tracks);
                await this.webConferenceService.changeCurrentPresentation({
                   type: SharingTypes.Screen,
                   userId: this.currentUserProfile.userId as number,
                   data: {} as any
                });
            }
            else {
                showCantJoinShareMessage();
            }
        }
        catch (e) {
            await this.$store.dispatch('reportError', e);

            if (e.code == 'INVALID_OPERATION') {
                showCantShareMessage();
            }

            if (this.webConferenceService.webConferenceData?.hasSharingType(SharingTypes.Screen)) {
                await this.webConferenceService.nullCurrentPresentation();
            }
        }
    }

    async stopSharing() {
        if (this.isSharingBeginStopped) {return;}

        this.isSharingBeginStopped = true;
        try {
            if (this.isSharingScreen()) {
                await this.joinHandler.shareClient.leave();
            }

            if (this.webConferenceService.webConferenceData?.currentPresentation != null) {
                await this.webConferenceService.nullCurrentPresentation();
            }
        }
        finally {
            this.isSharingBeginStopped = false;
        }
    }

    @Watch('isThereSharingTracks')
    onSharingTracksChanged() {
        if (this.webConferenceService.webConferenceData?.hasSharingType(SharingTypes.Screen) &&
            this.isSharing && !this.isThereSharingTracks)
        {
            this.stopSharing();
        }
    }

    @Watch('isCurrentPresentationActive')
    onIsCurrentPresentationActiveChanged() {
        if (!this.isCurrentPresentationActive && this.isSharingScreen()) {
            this.stopSharing();
        }
    }
}
